import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { X, Loader2, Plus, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Trash2, Save, Link2, Play, SquareDot, ZoomIn, ZoomOut, CheckCircle, Film, Zap, CopyPlus } from 'lucide-react'
import { dedupAiKeywordAgainstLlm } from './dedupMoments'
import { useHighlightsApi } from './useHighlightsApi'
import TrimRangeSlider from './TrimRangeSlider'
import SourcePickerModal from './SourcePickerModal'
import { useReject } from './useReject'
import { RejectMomentDialog, RejectUndoToast } from './RejectMomentDialog'
import { useApprove } from './useApprove'
import { ApproveUndoToast } from './ApproveMomentControl'

// Full-screen editor for an existing reel.
//
// Loads the current segments, renders each as a card with a Mux Player
// preview + dual-handle trim slider + reorder + delete. Insert buttons
// between every pair (and at the head + tail) open the SourcePickerModal
// to add a new segment. Save fires PUT /reels/:id/segments which replaces
// the list server-side and invalidates the edge cache so the same share
// URL serves fresh content immediately.

function fmtTime(ms) {
  const s = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

// Time input format: hh:mm:ss.ss when ≥1h, otherwise mm:ss.ss
// (centisecond precision). Always two decimals so the field never reflows
// width as the user types. Matches the Mux Player's display format so the
// In/Out readouts read consistently against the player progress bar.
function fmtTimeInput(ms) {
  const total = Math.max(0, ms / 1000)
  const hrs = Math.floor(total / 3600)
  const mins = Math.floor((total - hrs * 3600) / 60)
  const secs = total - hrs * 3600 - mins * 60
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${secs.toFixed(2).padStart(5, '0')}`
  }
  return `${mins}:${secs.toFixed(2).padStart(5, '0')}`
}
// Parses hh:mm:ss[.ss], mm:ss[.ss], m:ss[.ss], ss[.ss], or bare seconds.
// Returns null if unparseable so the caller can flag a bad input rather
// than commit garbage.
function parseTimeInput(raw) {
  if (raw == null) return null
  const t = String(raw).trim()
  if (!t) return null
  // hh:mm:ss[.ss]
  const hms = t.match(/^(\d+):(\d+):(\d+(?:\.\d+)?)$/)
  if (hms) {
    const hrs = parseInt(hms[1], 10)
    const mins = parseInt(hms[2], 10)
    const secs = parseFloat(hms[3])
    if (isNaN(hrs) || isNaN(mins) || isNaN(secs)) return null
    return Math.round((hrs * 3600 + mins * 60 + secs) * 1000)
  }
  // mm:ss[.ss]
  const ms = t.match(/^(\d+):(\d+(?:\.\d+)?)$/)
  if (ms) {
    const mins = parseInt(ms[1], 10)
    const secs = parseFloat(ms[2])
    if (isNaN(mins) || isNaN(secs)) return null
    return Math.round((mins * 60 + secs) * 1000)
  }
  const bare = t.match(/^\d+(?:\.\d+)?$/)
  if (bare) return Math.round(parseFloat(t) * 1000)
  return null
}

// Local segment shape used in the editor's working list. Fields:
//   source_tbtv_id, start_ms, end_ms     — the canonical save shape
//   source_title, source_duration_seconds, source_thumbnail_url,
//   source_mux_playback_id               — display-only enrichment
//   _key                                 — stable React key (resists reorder)
// withKey also snapshots _orig_start_ms / _orig_end_ms (Step 25g) so the
// Reset button in SegmentCard can restore the segment to its pre-edit
// bounds — either the values stored in the DB at editor load, or the
// values supplied when the segment was just inserted via SourcePickerModal.
function withKey(s) {
  return {
    ...s,
    _key: s._key || `${s.source_tbtv_id}-${Math.random().toString(36).slice(2, 8)}`,
    _orig_start_ms: s._orig_start_ms != null ? s._orig_start_ms : s.start_ms,
    _orig_end_ms:   s._orig_end_ms   != null ? s._orig_end_ms   : s.end_ms,
  }
}

export default function ReelEditor({ reelId, onClose }) {
  const api = useHighlightsApi()
  const [reel, setReel] = useState(null)
  const [segments, setSegments] = useState(null)
  const [originalSnapshot, setOriginalSnapshot] = useState(null)
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState(null)
  const [savedAt, setSavedAt] = useState(null)
  const [picker, setPicker] = useState(null)  // { position } | null

  // Step 33 Phase 5.6: reel-level transitions state, tracked separately
  // from `segments` because saves go through a different endpoint
  // (patchReel) and dirty tracking is separate.
  const [transitionsEnabled, setTransitionsEnabled] = useState(false)
  const [originalTransitionsEnabled, setOriginalTransitionsEnabled] = useState(false)
  // Available bumper library — fetched once at editor open. Enables the
  // per-gap picker to show real bumper names + durations without the
  // editor having to type registry keys. Empty array when the transitions
  // registry is empty or the fetch fails (per-gap picker falls back to
  // showing "None" only).
  const [availableTransitions, setAvailableTransitions] = useState([])

  // Step 26.5: reject hook shared by every SegmentCard. Rejecting from a
  // segment writes to the per-video drop list — does NOT delete the
  // segment from this reel (editor can still trim/move/delete it as a
  // segment via the existing controls). The effect is "skip this moment
  // on future TBTV-AI re-runs of this video."
  const reject = useReject()
  // Single-player lock: at most one segment's <mux-player> is allowed to be
  // playing at a time. When a card starts playing it calls notifyPlayStart
  // with its player element; we pause any previously-tracked element first,
  // then remember the new one. A card calls notifyPlayStop on pause so we
  // don't keep stale refs. Lives at the editor level because the cards
  // don't know about each other.
  const activePlayerRef = useRef(null)
  const notifyPlayStart = useCallback((el) => {
    const prev = activePlayerRef.current
    if (prev && prev !== el) {
      try { prev.pause() } catch { /* ignore */ }
    }
    activePlayerRef.current = el
  }, [])
  const notifyPlayStop = useCallback((el) => {
    if (activePlayerRef.current === el) activePlayerRef.current = null
  }, [])
  // Step 27 Option B: approve hook. Same shape — does NOT modify the
  // segment, just records the underlying AI moment as a gold label.
  const approve = useApprove()
  // Reel Pool = key moments + chapters from every distinct source video that
  // already contributes a segment to this reel. Powers the default tab in
  // SourcePickerModal so editors can grab moments they skipped without
  // re-running the search or reopening the per-video composer.
  const [reelPool, setReelPool] = useState(null)         // [{ kind, source_tbtv_id, source_title, source_mux_playback_id, ...row fields }]
  const [sourceVideos, setSourceVideos] = useState([])    // distinct, ordered by first appearance

  useEffect(() => {
    let cancelled = false
    api.getReel(reelId)
      .then(r => {
        if (cancelled) return
        setReel(r)
        const segs = (r.segments || []).map(s => withKey({
          source_tbtv_id: s.source_tbtv_id,
          start_ms: s.start_ms,
          end_ms: s.end_ms,
          source_title: s.source_title,
          source_duration_seconds: s.source_duration_seconds,
          source_thumbnail_url: s.source_thumbnail_url,
          source_mux_playback_id: s.source_mux_playback_id,
          transition_out: s.transition_out ?? null,
        }))
        setSegments(segs)
        setOriginalSnapshot(JSON.stringify(segs.map(({ _key, _orig_start_ms, _orig_end_ms, ...rest }) => rest)))

        // Step 33 Phase 5.6: hydrate reel-level transitions state.
        // Server sends 0/1 (SQLite INTEGER) — coerce to boolean.
        const te = r.transitions_enabled === 1 || r.transitions_enabled === true
        setTransitionsEnabled(te)
        setOriginalTransitionsEnabled(te)

        // Derive distinct source videos by source_tbtv_id, preserving first-seen
        // order so the picker chips line up with the reel's narrative order.
        const seen = new Set()
        const sources = []
        for (const s of segs) {
          if (!s.source_tbtv_id || seen.has(s.source_tbtv_id)) continue
          seen.add(s.source_tbtv_id)
          sources.push({
            tbtv_id: s.source_tbtv_id,
            title: s.source_title,
            duration_seconds: s.source_duration_seconds,
            thumbnail_url: s.source_thumbnail_url,
            mux_playback_id: s.source_mux_playback_id,
          })
        }
        if (cancelled) return
        setSourceVideos(sources)

        // If this reel was built from a search, re-run each query and pull
        // every cue match within the reel's source videos into the pool —
        // so the editor sees the same "search hits" they had at build time.
        // origin shape: { kind: 'search', queries: ["..."], mode }
        const searchQueries = (r.origin?.kind === 'search' && Array.isArray(r.origin.queries))
          ? r.origin.queries.filter(Boolean)
          : []

        // Fetch key moments + chapters per source video in parallel. Tolerate
        // per-video failures (deleted videos, transient 5xx) by skipping that
        // source — the remaining pool stays usable. Endpoint shapes:
        //   keyMoments → { moments: [{ ord, start_ms, end_ms, title, ... }] }
        //   chapters   → { chapters: [{ ord, start_ms, title }] }   (no end_ms)
        const sourceIds = new Set(sources.map(s => s.tbtv_id))
        Promise.all([
          // Per-video key moments + chapters.
          Promise.all(sources.map(v => Promise.all([
            api.keyMoments(v.tbtv_id).catch(() => ({ moments: [] })),
            api.chapters(v.tbtv_id).catch(() => ({ chapters: [] })),
          ]))),
          // Original search query re-run, one fetch per distinct query. Cap
          // limit high so we get the full hit set across all source videos
          // (the worker pages by video, not by cue). Filter to source videos
          // client-side so unrelated catalog hits don't leak in.
          Promise.all(searchQueries.map(q =>
            api.search(q, { limit: 50, mode: r.origin?.mode || 'both' })
              .then(res => ({ q, videos: res?.videos || [] }))
              .catch(() => ({ q, videos: [] }))
          )),
        ]).then(([perVideoResults, searchResults]) => {
          if (cancelled) return
          const pool = []
          // Lowercase queries once for the KM/chapter text-match pass below.
          const queryNeedles = searchQueries.map(q => q.trim().toLowerCase()).filter(Boolean)
          // Match an AI key moment against the original search queries by
          // scanning its title + narratives + concept tags. Returns the
          // matched query strings (so the UI can show which query hit).
          function matchedQueriesFor(km) {
            if (queryNeedles.length === 0) return null
            const haystack = [
              km.title,
              km.audible_narrative,
              km.visual_narrative,
              Array.isArray(km.concepts) ? km.concepts.join(' ') : '',
            ].filter(Boolean).join(' ').toLowerCase()
            if (!haystack) return null
            const hits = queryNeedles.filter(q => haystack.includes(q))
            return hits.length > 0 ? hits : null
          }

          perVideoResults.forEach(([kmRes, chRes], i) => {
            const v = sources[i]
            // Step 25g: suppress AI-keyword moments contained in AI-LLM
            // spans before populating the Reel Pool — same play, two rows.
            const dedupedKm = dedupAiKeywordAgainstLlm(kmRes.moments || [])
            for (const m of dedupedKm) {
              pool.push({
                kind: 'key_moment',
                source_tbtv_id: v.tbtv_id,
                source_title: v.title,
                source_duration_seconds: v.duration_seconds,
                source_thumbnail_url: v.thumbnail_url,
                source_mux_playback_id: v.mux_playback_id,
                row_id: `km:${v.tbtv_id}:${m.ord}`,
                start_ms: m.start_ms,
                end_ms: m.end_ms,
                title: m.title,
                // Carry narrative + visual_narrative so the segment card in
                // the editor can render "what is this clip?" without
                // needing another DB lookup.
                audible_narrative: m.audible_narrative,
                visual_narrative: m.visual_narrative,
                moment_type: m.moment_type,
                quality_score: m.quality_score,
                source: m.source,
                detector: m.detector,
                // Set when the KM's text matches one of the original
                // search queries — the picker renders a badge so the
                // editor can see at a glance which curated moments line
                // up with the search that built this reel.
                matched_queries: matchedQueriesFor(m),
              })
            }
            for (const c of (chRes.chapters || [])) {
              pool.push({
                kind: 'chapter',
                source_tbtv_id: v.tbtv_id,
                source_title: v.title,
                source_duration_seconds: v.duration_seconds,
                source_thumbnail_url: v.thumbnail_url,
                source_mux_playback_id: v.mux_playback_id,
                row_id: `ch:${v.tbtv_id}:${c.ord}`,
                start_ms: c.start_ms,
                // Chapters don't carry an end_ms; default to a 20s window so
                // the trim pre-fill has a sensible range. Editor can adjust.
                end_ms: c.end_ms != null ? c.end_ms : c.start_ms + 20000,
                title: c.title,
                source: c.source,
              })
            }
          })

          // Cue hits from re-running the original search. Use ±10s windows
          // for the trim pre-fill so they slot in like the original cart
          // segments did. Restrict to source videos only.
          const PRE_MS = 10000, POST_MS = 10000
          for (const { q, videos } of searchResults) {
            for (const vid of videos) {
              if (!sourceIds.has(vid.tbtv_id)) continue
              const src = sources.find(s => s.tbtv_id === vid.tbtv_id)
              for (const m of (vid.matches || [])) {
                pool.push({
                  kind: 'search_hit',
                  source_tbtv_id: vid.tbtv_id,
                  source_title: src?.title || vid.title,
                  source_duration_seconds: src?.duration_seconds || vid.duration,
                  source_thumbnail_url: src?.thumbnail_url || vid.thumbnail_url,
                  source_mux_playback_id: src?.mux_playback_id,
                  row_id: `q:${q}:${vid.tbtv_id}:${m.start_ms}`,
                  start_ms: Math.max(0, m.start_ms - PRE_MS),
                  end_ms: m.start_ms + POST_MS,
                  // Anchor timestamp — what's shown to the editor (the cue's
                  // own start), distinct from the ±10s window we'll trim to.
                  anchor_ms: m.start_ms,
                  title: q,
                  excerpt: m.excerpt || m.text,
                  match_source: m.source,     // 'exact' | 'phonetic' | 'trigram'
                  in_key_moment: m.in_key_moment,
                })
              }
            }
          }

          setReelPool(pool)
        })
      })
      .catch(e => { if (!cancelled) setErr(e.message) })
    return () => { cancelled = true }
  }, [reelId])

  // Step 33 Phase 5.6: fetch available bumper library ONCE at open.
  // Used by the per-gap picker to populate the dropdown. Silent-fail
  // (empty array) if the registry is empty or the fetch errors — the
  // picker still renders with just "None" as an option, and per-gap
  // transitions can still be cleared even without knowing the full list.
  useEffect(() => {
    let cancelled = false
    api.listReelTransitionsAvailable()
      .then(r => {
        if (cancelled) return
        setAvailableTransitions(r?.transitions || [])
      })
      .catch(() => {
        if (cancelled) return
        setAvailableTransitions([])
      })
    return () => { cancelled = true }
  }, [api])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !picker) onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, picker])

  // Split dirty into segments-vs-reel-fields because they save through
  // different endpoints (updateReelSegments vs patchReel). Save() calls
  // whichever endpoint(s) need it; the Save button is enabled if EITHER
  // is dirty.
  const segmentsDirty = useMemo(() => {
    if (!segments || !originalSnapshot) return false
    return JSON.stringify(segments.map(({ _key, _orig_start_ms, _orig_end_ms, ...rest }) => rest)) !== originalSnapshot
  }, [segments, originalSnapshot])
  const reelDirty = useMemo(() => {
    return transitionsEnabled !== originalTransitionsEnabled
  }, [transitionsEnabled, originalTransitionsEnabled])
  const dirty = segmentsDirty || reelDirty

  // Flip the reel-level transitions toggle. On enable, offer to apply the
  // default bumper (if the admin has flagged one in the Bumper Library) to
  // every transition point in one prompt — avoids the tedious per-gap
  // dropdown click when the editor just wants "the same bumper everywhere".
  function handleTransitionsToggle(nextEnabled) {
    setTransitionsEnabled(nextEnabled)
    if (!nextEnabled) return
    const defaultBumper = availableTransitions.find(t => t.is_default === 1)
    const gapCount = segments ? Math.max(0, segments.length - 1) : 0
    if (!defaultBumper || gapCount === 0) return
    const proceed = window.confirm(
      `Apply default bumper "${defaultBumper.label}" (${defaultBumper.duration_seconds}s) ` +
      `to all ${gapCount} transition point${gapCount === 1 ? '' : 's'}?\n\n` +
      `Existing per-gap selections will be overwritten. You can still change ` +
      `individual gaps after applying.`
    )
    if (!proceed) return
    // transition_out sits on the segment BEFORE the gap, so the last
    // segment (which has no gap after it) is left alone.
    setSegments(prev => prev.map((s, i) => (
      i < prev.length - 1 ? { ...s, transition_out: defaultBumper.key } : s
    )))
  }

  function updateSegment(idx, patch) {
    setSegments(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  }
  function deleteSegment(idx) {
    setSegments(prev => prev.filter((_, i) => i !== idx))
  }
  function moveSegment(idx, dir) {
    setSegments(prev => {
      const next = [...prev]
      const tgt = idx + dir
      if (tgt < 0 || tgt >= next.length) return prev
      ;[next[idx], next[tgt]] = [next[tgt], next[idx]]
      return next
    })
  }
  // Duplicate a segment (same source + trim) right after the original, with a fresh key so it's an
  // independent row the operator can re-trim and move. Marks the reel dirty (a new segment) → save enabled.
  function duplicateSegment(idx) {
    setSegments(prev => {
      const src = prev[idx]
      if (!src) return prev
      const { _key, ...rest } = src
      const copy = withKey({ ...rest }) // withKey mints a new _key since none is passed
      const next = [...prev]
      next.splice(idx + 1, 0, copy)
      return next
    })
  }
  function insertAt(position, seg) {
    const enriched = withKey({
      source_tbtv_id: seg.source_tbtv_id,
      start_ms: seg.start_ms,
      end_ms: seg.end_ms,
      source_title: seg._meta?.source_title,
      source_duration_seconds: seg._meta?.source_duration_seconds,
      source_thumbnail_url: seg._meta?.source_thumbnail_url,
      source_mux_playback_id: seg._meta?.source_mux_playback_id,
    })
    setSegments(prev => {
      const next = [...prev]
      if (position === 'start') next.unshift(enriched)
      else if (position === 'end') next.push(enriched)
      else next.splice(position + 1, 0, enriched)
      return next
    })
  }

  async function save() {
    if (!segments || segments.length === 0) {
      setSaveErr('Reel must have at least one segment.')
      return
    }
    setSaving(true)
    setSaveErr(null)
    try {
      // Save reel-level fields first (transitions_enabled). Kept as a
      // separate call from segments so a segments-only edit still fires
      // exactly one endpoint. If both are dirty, both fire — the server's
      // patchReel handles transitions_enabled cache-invalidation and
      // updateReelSegments does its own. Order matters slightly: if the
      // editor is toggling transitions_enabled OFF and clearing per-segment
      // transition_out values in the same save, doing patch first means
      // the read-side already reflects "off" while segments still hold
      // stale transition_out (harmless — stitcher short-circuits when off).
      if (reelDirty) {
        await api.patchReel(reelId, { transitions_enabled: transitionsEnabled ? 1 : 0 })
        setOriginalTransitionsEnabled(transitionsEnabled)
      }

      if (segmentsDirty) {
        const payload = segments.map(s => ({
          source_tbtv_id: s.source_tbtv_id,
          start_ms: s.start_ms,
          end_ms: s.end_ms,
          // Step 33: per-segment "transition out of this clip" registry key.
          // Full-replace semantics on the server would null this out if we
          // dropped it from the payload — sending it every save preserves
          // whatever the editor set (or last-loaded from the server).
          transition_out: s.transition_out ?? null,
        }))
        await api.updateReelSegments(reelId, payload)
        // Refresh snapshot so dirty flag clears and subsequent edits compute
        // against the new baseline. MUST strip the same synthetic fields as
        // the initial-load snapshot (line ~149) and the dirty comparator
        // (line ~317) — otherwise `_orig_*` end up baked into the snapshot
        // but stripped from `segments`, and the comparator flags dirty=true
        // forever (Save button stays green after every successful save).
        setOriginalSnapshot(JSON.stringify(segments.map(({ _key, _orig_start_ms, _orig_end_ms, ...rest }) => rest)))
      }

      setSavedAt(Date.now())
      setTimeout(() => setSavedAt(null), 4000)
    } catch (e) {
      setSaveErr(e.message)
    } finally {
      setSaving(false)
    }
  }

  const shareUrl = reel?.playback_url

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0a0a', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {reel?.title || (reel ? reelId : 'Loading…')}
          </div>
          <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', marginTop: 2 }}>
            {reelId}{segments && ` · ${segments.length} segment${segments.length === 1 ? '' : 's'}`}{dirty && <span style={{ color: '#f59e0b' }}> · unsaved changes</span>}
          </div>
        </div>
        {/* Step 33 Phase 5.6: master toggle for reel-level transitions.
            Disabled state is a hard cut between clips (identical to the
            pre-Step-33 behavior). When ON, the stitcher honors per-segment
            transition_out values chosen in the per-gap picker below. */}
        {reel && (
          <label title={transitionsEnabled
            ? 'On: bumpers appear between clips where a per-gap choice is set'
            : 'Off: hard cuts between all clips (per-gap choices are ignored by the stitcher)'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px', border: '1px solid ' + (transitionsEnabled ? '#3b82f6' : '#333'),
              background: transitionsEnabled ? 'rgba(59,130,246,0.08)' : 'transparent',
              borderRadius: 4, fontSize: 12, cursor: 'pointer',
              color: transitionsEnabled ? '#93c5fd' : '#888',
            }}>
            <Film size={12} />
            <input type="checkbox" checked={transitionsEnabled}
              onChange={e => handleTransitionsToggle(e.target.checked)}
              style={{ margin: 0, cursor: 'pointer' }} />
            Transitions
          </label>
        )}
        {shareUrl && (
          <button onClick={() => navigator.clipboard?.writeText(shareUrl)} title="Copy share URL"
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Link2 size={12} /> Copy URL
          </button>
        )}
        {shareUrl && (
          <a href={shareUrl} target="_blank" rel="noopener noreferrer" title="Open share URL (may serve cached content for up to 1 hour after a save)"
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
            <Play size={12} /> Open
          </a>
        )}
        {/* Step 33 Phase 5.6+: editor-only fresh-preview button that
            bypasses the stitcher's edge cache via ?no_cache=1. Solves the
            "I saved but I'm still seeing bumpers I removed" trap that
            hit us right after Phase 5.6 shipped. Points at the direct
            stitcher URL because the watch → vader proxy chain strips
            query params and adds its own CF cache layer, so ?no_cache=1
            wouldn't survive the canonical share URL. Amber-tinted so
            editors can tell at a glance it's not the shareable URL.
            The &_ts=... cache-buster is essential: the stitcher's
            response has Cache-Control: max-age=3600, so a deterministic
            Fresh URL would be served straight from the browser's HTTP
            cache on the second click. Each click gets a fresh timestamp
            → unique URL → forces a new fetch to the stitcher. */}
        <a
          href={`https://hls-stitcher.todd-dff.workers.dev/reels/${reelId}/stream.m3u8?no_cache=1&_ts=${Date.now()}`}
          onClick={(e) => {
            // Re-stamp the timestamp at click time so multiple clicks
            // in the same session (without a page rerender) still bust
            // cache. The initial href renders with mount-time timestamp;
            // click-time re-stamp keeps every click fresh.
            e.currentTarget.href = `https://hls-stitcher.todd-dff.workers.dev/reels/${reelId}/stream.m3u8?no_cache=1&_ts=${Date.now()}`
          }}
          target="_blank"
          rel="noopener noreferrer"
          title="Editor-only: open with cache bypass. Shows latest save immediately. Direct stitcher URL — do not share this link with end users."
          style={{
            padding: '6px 10px', background: 'transparent',
            border: '1px solid #7c2d12', borderRadius: 4,
            color: '#fdba74', cursor: 'pointer', fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            textDecoration: 'none',
          }}>
          <Zap size={12} /> Fresh
        </a>
        <button onClick={save} disabled={!dirty || saving || !segments?.length}
          style={{
            padding: '8px 16px',
            background: !dirty || !segments?.length ? '#222' : '#22c55e',
            border: 'none', borderRadius: 4, color: 'white',
            cursor: !dirty || !segments?.length ? 'default' : 'pointer',
            fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
          }}>
          {saving ? <Loader2 size={13} className="spin" /> : <Save size={13} />}
          {savedAt ? 'Saved' : 'Save'}
        </button>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {err && <div style={{ color: 'crimson', padding: 12 }}>{err}</div>}
        {saveErr && <div style={{ color: 'crimson', padding: 12, background: 'rgba(220,38,38,0.08)', border: '1px solid #4a1414', borderRadius: 4, marginBottom: 12 }}>Save failed: {saveErr}</div>}
        {!reel && !err && (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Loader2 size={14} className="spin" /> Loading reel…
          </div>
        )}

        {segments && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 880, margin: '0 auto' }}>
            <InsertButton onClick={() => setPicker({ position: 'start' })} label="Insert at start" />
            {segments.map((s, i) => (
              <div key={s._key}>
                <SegmentCard
                  segment={s}
                  index={i}
                  total={segments.length}
                  matchedMoment={findMatchedMoment(s, reelPool)}
                  onChange={(patch) => updateSegment(i, patch)}
                  onDelete={() => deleteSegment(i)}
                  onDuplicate={() => duplicateSegment(i)}
                  onMove={(dir) => moveSegment(i, dir)}
                  onReject={(m) => reject.start(m)}
                  onApprove={(m) => approve.start(m)}
                  onPlayStart={notifyPlayStart}
                  onPlayStop={notifyPlayStop}
                />
                {i < segments.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 4 }}>
                    <InsertButton onClick={() => setPicker({ position: i })} label="Insert between" small />
                    {transitionsEnabled && (
                      <GapTransitionSelector
                        value={s.transition_out ?? ''}
                        onChange={(v) => updateSegment(i, { transition_out: v || null })}
                        available={availableTransitions}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
            <InsertButton onClick={() => setPicker({ position: 'end' })} label="Insert at end" />
          </div>
        )}
      </div>

      {picker && (
        <SourcePickerModal
          position={picker.position}
          onClose={() => setPicker(null)}
          onInsert={(seg) => insertAt(picker.position, seg)}
          reelPool={reelPool}
          sourceVideos={sourceVideos}
          currentSegments={segments}
        />
      )}

      {/* Step 26.5: shared reject dialog + undo toast for the Reel Editor. */}
      <RejectMomentDialog hook={reject} />
      <RejectUndoToast hook={reject} />
      {/* Step 27 Option B: approve undo toast (offset above reject). */}
      <ApproveUndoToast hook={approve} offsetBottom={80} />

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  )
}

function InsertButton({ onClick, label, small }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: small ? '2px 0' : '8px 0' }}>
      <button onClick={onClick}
        style={{
          padding: small ? '3px 10px' : '6px 14px',
          background: 'transparent',
          border: '1px dashed #333',
          borderRadius: 999, color: '#888', cursor: 'pointer',
          fontSize: small ? 11 : 12,
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
        <Plus size={small ? 11 : 12} /> {label}
      </button>
    </div>
  )
}

// Step 33 Phase 5.6: per-gap transition picker. Only rendered when the
// reel's master transitions_enabled is on. Writes to the segment BEFORE
// the gap (matches the stitcher's "transition_out of this clip" model).
//
// `available` may be empty if the registry is empty or the fetch failed
// at editor open — in that case the dropdown still shows "None" as an
// option and any preexisting value gets rendered as a raw key so editors
// can at least see what's set (and clear it if they want).
function GapTransitionSelector({ value, onChange, available }) {
  const hasKnown = available.some(t => t.key === value)
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '2px 8px', border: '1px solid ' + (value ? '#3b82f6' : '#2a2a2a'),
      background: value ? 'rgba(59,130,246,0.06)' : 'transparent',
      borderRadius: 999, fontSize: 11,
      color: value ? '#93c5fd' : '#666',
    }}>
      <Film size={10} />
      <span>Bumper:</span>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{
          background: 'transparent', color: 'inherit', border: 'none',
          fontSize: 11, cursor: 'pointer', outline: 'none',
        }}>
        <option value="" style={{ background: '#1a1a1a', color: '#ddd' }}>None</option>
        {available.map(t => (
          <option key={t.key} value={t.key} style={{ background: '#1a1a1a', color: '#ddd' }}>
            {t.label} ({t.duration_seconds}s)
          </option>
        ))}
        {value && !hasKnown && (
          <option value={value} style={{ background: '#1a1a1a', color: '#f59e0b' }}>
            {value} (unknown key)
          </option>
        )}
      </select>
    </label>
  )
}

// Look up the key moment or chapter that this segment was built from by
// matching against the reel pool (KMs + chapters from every source video
// already contributing to this reel). Returns the first pool entry whose
// anchor (start_ms) falls within the segment's clip window. Prefers
// key_moments over chapters when both match. Null if the segment was
// added as a raw clip (e.g. from Content Library) with no curated origin.
function findMatchedMoment(segment, reelPool) {
  if (!reelPool || reelPool.length === 0) return null
  let kmHit = null, chHit = null
  for (const p of reelPool) {
    if (p.source_tbtv_id !== segment.source_tbtv_id) continue
    if (p.start_ms < segment.start_ms || p.start_ms > segment.end_ms) continue
    if (p.kind === 'key_moment' && !kmHit) { kmHit = p; }
    else if (p.kind === 'chapter' && !chHit) { chHit = p; }
    if (kmHit) break  // KM beats chapter
  }
  return kmHit || chHit || null
}

function SegmentCard({ segment, index, total, matchedMoment, onChange, onDelete, onDuplicate, onMove, onReject, onApprove, onPlayStart, onPlayStop }) {
  const durationMs = (segment.source_duration_seconds || 0) * 1000
  const playbackId = segment.source_mux_playback_id

  // ─── Zoom control for the trim slider ─────────────────────────────────
  // `zoom` is a 0..1 number. 0 = show full source on the trim track.
  // 1 = show just enough to comfortably frame the clip (clip duration + 40%
  // padding either side, clamped to a minimum 10s view window).
  // Default 0.5 so first-time editing is already moderately zoomed.
  const [zoom, setZoom] = useState(0.5)
  // Step 25g: freeze the view bounds during a slider drag so the OTHER
  // handle doesn't appear to slide while the user is dragging one. Without
  // this freeze, view re-centers on the clip midpoint on every onChange,
  // which moves both handle pixel positions even though only one bound
  // changed. Set on slider's onDragStart; cleared on onChangeCommitted.
  const [frozenView, setFrozenView] = useState(null)
  const clipDurMs = Math.max(0, segment.end_ms - segment.start_ms)
  const clipCenterMs = (segment.start_ms + segment.end_ms) / 2

  // Min view = clip + 40% padding each side, never below 10s. Max view =
  // full source. Exponential interpolation feels right for a zoom control:
  // small slider movements at the wide end make big context changes, fine
  // adjustments at the tight end keep handles maneuverable.
  const minView = Math.max(10000, clipDurMs * 1.8)
  const maxView = Math.max(minView, durationMs)
  const viewWidthMs = durationMs > 0
    ? maxView * Math.pow(minView / maxView, zoom)
    : 0

  // Center the visible view on the clip midpoint and clamp to source bounds.
  // If the centered window would extend past the source, slide it back.
  let viewMinMs = 0, viewMaxMs = durationMs
  if (durationMs > 0 && viewWidthMs < durationMs) {
    let vmin = Math.max(0, clipCenterMs - viewWidthMs / 2)
    let vmax = vmin + viewWidthMs
    if (vmax > durationMs) { vmax = durationMs; vmin = Math.max(0, vmax - viewWidthMs) }
    viewMinMs = vmin
    viewMaxMs = vmax
  }
  // If a slider drag is in progress, use the snapshot taken at drag start
  // so the view doesn't slide as the clip bounds change.
  if (frozenView) {
    viewMinMs = frozenView.viewMinMs
    viewMaxMs = frozenView.viewMaxMs
  }
  // Player loads the FULL source (Step 25g) — previously the URL carried
  // asset_start_time/asset_end_time and the React `key` included the trim
  // bounds, so every slider tick re-mounted the player and reloaded HLS,
  // which made the preview lag/freeze during edits. Now the player stays
  // mounted; edits seek it to the relevant edge via the effect below.
  // currentTime is in absolute source seconds, no offset needed.
  const srcUrl = playbackId
    ? `https://stream.mux.com/${playbackId}.m3u8`
    : null
  const playerRef = useRef(null)
  const playerReadyRef = useRef(false)
  const prevBoundsRef = useRef({ start_ms: segment.start_ms, end_ms: segment.end_ms })
  // Latest bounds in a ref so the timeupdate listener (registered once) can
  // read the current end_ms without re-binding every edit.
  const boundsRef = useRef({ start_ms: segment.start_ms, end_ms: segment.end_ms })
  boundsRef.current = { start_ms: segment.start_ms, end_ms: segment.end_ms }
  // "Play clip" mode — when true, auto-pause when playhead crosses Out.
  // Cleared on any user-initiated seek so manual scrubbing past Out doesn't
  // get hijacked by the auto-pause.
  const playingClipRef = useRef(false)
  // Guards our OWN programmatic seeks (Play clip / seek-to-In) so `onSeeking` doesn't treat them as a user
  // scrub and cancel play-clip mode. Also removes a seek/play event-ordering race that made Play clip miss
  // the auto-stop on deep-offset seeks (inserted clips in long sources).
  const internalSeekRef = useRef(false)
  // Current player time in ms (absolute source coords). Drives the playhead
  // indicator inside the trim slider so the editor can see where in the clip
  // playback is. Updated from the existing timeupdate listener (~4Hz) and
  // also on seek/pause/loadedmetadata so the indicator stays current when
  // the editor scrubs without playing.
  const [playheadMs, setPlayheadMs] = useState(null)

  // Seek to the In frame once the player has loaded its metadata, and
  // auto-pause whenever playback crosses the Out boundary (regardless of
  // whether playback started from "Play clip" or the native play control).
  // Manual scrub past Out is allowed — `seeking` resets a flag that's set
  // by `play`, so a scrub doesn't trigger an immediate pause.
  useEffect(() => {
    const el = playerRef.current
    if (!el) return
    playerReadyRef.current = false
    const onMeta = () => {
      playerReadyRef.current = true
      internalSeekRef.current = true // our seek-to-In — not a user scrub
      try { el.currentTime = segment.start_ms / 1000 } catch { /* not ready yet */ }
      setPlayheadMs(Math.round((el.currentTime || 0) * 1000))
    }
    // Auto-pause-at-Out is gated on playingClipRef (set by the ▶ Play clip button): Play clip previews the
    // trimmed selection — plays In→Out then stops. The native ▶ plays the whole source past Out. ONLY a
    // genuine USER scrub on the native bar cancels play-clip mode; our own programmatic seeks (Play clip /
    // seek-to-In / edge edits) are flagged via internalSeekRef so they don't — which also removes a
    // seek/play event-ordering race that made Play clip miss the stop on deep seeks (inserted clips) and
    // after Set In/Out (a large backward seek to In). internalSeekRef stays true until the seek completes.
    const onPlay = () => {
      onPlayStart?.(el)  // tell the editor this card just took the play lock
    }
    const onPause = () => onPlayStop?.(el)
    const onEnded = () => onPlayStop?.(el)
    const onSeeking = () => {
      if (!internalSeekRef.current) playingClipRef.current = false // user native-bar scrub cancels the auto-stop
      setPlayheadMs(Math.round((el.currentTime || 0) * 1000))
    }
    const onTimeUpdate = () => {
      setPlayheadMs(Math.round((el.currentTime || 0) * 1000))
      if (!playingClipRef.current) return
      const t = el.currentTime
      const endSec = boundsRef.current.end_ms / 1000
      if (t >= endSec) {
        try { el.pause() } catch { /* ignore */ }
        try { el.currentTime = endSec } catch { /* ignore */ }
        playingClipRef.current = false
      }
    }
    const onSeeked = () => {
      internalSeekRef.current = false // our seek finished; a later 'seeking' is a real user scrub
      setPlayheadMs(Math.round((el.currentTime || 0) * 1000))
    }
    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('ended', onEnded)
    el.addEventListener('timeupdate', onTimeUpdate)
    el.addEventListener('seeking', onSeeking)
    el.addEventListener('seeked', onSeeked)
    return () => {
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('timeupdate', onTimeUpdate)
      el.removeEventListener('seeking', onSeeking)
      el.removeEventListener('seeked', onSeeked)
      onPlayStop?.(el)  // releasing the card (delete/reorder/unmount) clears the lock
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackId])

  // Triggered by the ▶ Play clip button: seek to In, then play. The auto-
  // pause-at-Out listener takes over from there. Internal seek must not
  // trip the onSeeking → playingClipRef = false escape, so we set the
  // flag AFTER the seek's seeking event has fired.
  function playClip() {
    const el = playerRef.current
    if (!el) return
    try {
      internalSeekRef.current = true // guard our seek-to-In so onSeeking won't cancel play-clip mode
      el.currentTime = segment.start_ms / 1000
      playingClipRef.current = true
      const p = el.play?.()
      if (p && typeof p.catch === 'function') p.catch(() => {})
    } catch { /* ignore */ }
  }

  // Click-to-seek from the trim slider — move the preview playhead to `ms` (absolute source coords).
  // Clears play-clip mode so the auto-pause-at-Out doesn't yank a manual scrub back.
  function seekTo(ms) {
    const el = playerRef.current
    if (!el) return
    playingClipRef.current = false
    try { el.currentTime = ms / 1000 } catch { /* ignore */ }
    setPlayheadMs(Math.round(ms))
  }

  // When the editor adjusts an edge (slider, chevron, Reset, timecode input,
  // Set In/Out), seek the preview to the edge that changed so the visible
  // frame matches the edit. Both-changed (Reset / insert) → seek to In.
  useEffect(() => {
    const el = playerRef.current
    if (!el || !playerReadyRef.current) return
    const prev = prevBoundsRef.current
    const startChanged = prev.start_ms !== segment.start_ms
    const endChanged = prev.end_ms !== segment.end_ms
    prevBoundsRef.current = { start_ms: segment.start_ms, end_ms: segment.end_ms }
    if (!startChanged && !endChanged) return
    let targetSec
    if (endChanged && !startChanged) {
      // Out moved — show the last frame of the clip (1s buffer so it's visible).
      targetSec = Math.max(0, (segment.end_ms - 1000) / 1000)
    } else {
      // In moved, or both moved (Reset) — show the In frame.
      targetSec = segment.start_ms / 1000
    }
    internalSeekRef.current = true // programmatic edge-edit seek — not a user scrub
    try { el.currentTime = targetSec } catch { /* ignore */ }
  }, [segment.start_ms, segment.end_ms])

  // Absolute timestamp (in source coordinates) of the current playhead.
  // Now that the player loads the full source, currentTime IS the absolute
  // source second — no need to add segment.start_ms anymore.
  function absolutePlayheadMs() {
    const t = playerRef.current?.currentTime
    if (t == null || isNaN(t)) return null
    return Math.round(t * 1000)
  }

  function setIn() {
    const p = absolutePlayheadMs()
    if (p == null) return
    const ns = Math.min(Math.max(p, 0), segment.end_ms - 250)
    onChange({ start_ms: ns, end_ms: segment.end_ms })
  }
  function setOut() {
    const p = absolutePlayheadMs()
    if (p == null) return
    const ne = Math.max(p, segment.start_ms + 250)
    onChange({ start_ms: segment.start_ms, end_ms: ne })
  }

  // Step 25g: nudge the In/Out by ±N ms. Replaces auto-padding at reel
  // build time — editor opts-in to padding per segment in the editor.
  function shiftIn(deltaMs) {
    const ns = Math.min(Math.max(segment.start_ms + deltaMs, 0), segment.end_ms - 250)
    onChange({ start_ms: ns, end_ms: segment.end_ms })
  }
  function shiftOut(deltaMs) {
    const cap = durationMs > 0 ? durationMs : Number.POSITIVE_INFINITY
    const ne = Math.max(Math.min(segment.end_ms + deltaMs, cap), segment.start_ms + 250)
    onChange({ start_ms: segment.start_ms, end_ms: ne })
  }

  return (
    <div style={{
      border: '1px solid #2a2a2a', borderRadius: 6, background: '#121212',
      padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: 4, background: '#0d1622',
          color: '#888', fontSize: 12, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {index + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Matched moment / chapter — the curated label for what this clip
              actually IS (e.g. "Dempsey goal", "Period 2 begins"). Falls
              back to the source video title when the segment was added as
              a raw clip with no curated origin. */}
          {matchedMoment ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: matchedMoment.kind === 'key_moment' ? '#22c55e' : '#fb923c', fontWeight: 700, letterSpacing: 0.3 }}>
                  {matchedMoment.kind === 'key_moment' ? '💫 KEY MOMENT' : '📑 CHAPTER'}
                </span>
                {matchedMoment.moment_type && (
                  <span style={{
                    fontSize: 9, padding: '0 5px', borderRadius: 8,
                    background: '#1f2937', color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.3,
                  }}>{matchedMoment.moment_type.replace('_', ' ')}</span>
                )}
                {matchedMoment.source === 'mux' && (
                  <span title="Mux Find Key Moments" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, background: 'rgba(251,146,60,0.15)', color: '#fb923c', fontWeight: 600 }}>M</span>
                )}
                {matchedMoment.source === 'tbtv_ai' && matchedMoment.detector === 'keyword' && (
                  <span title="TBTV-AI keyword sublayer" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontWeight: 600 }}>AI kw</span>
                )}
                {matchedMoment.source === 'tbtv_ai' && matchedMoment.detector === 'llm' && (
                  <span title="TBTV-AI LLM sublayer" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600 }}>AI LLM</span>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#dbe5f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {matchedMoment.title || '(untitled moment)'}
              </div>
              {matchedMoment.audible_narrative && (
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {matchedMoment.audible_narrative}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                from <span style={{ color: '#aaa' }}>{segment.source_title || segment.source_tbtv_id}</span>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {segment.source_title || segment.source_tbtv_id}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>
            {segment.source_tbtv_id} · source duration {fmtTime(durationMs)} · clip {fmtTime(segment.end_ms - segment.start_ms)}
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <button onClick={() => onMove(-1)} disabled={index === 0} title="Move up"
            style={{ padding: 4, background: 'transparent', border: '1px solid #333', borderRadius: 4, color: index === 0 ? '#444' : '#888', cursor: index === 0 ? 'default' : 'pointer' }}>
            <ChevronUp size={14} />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} title="Move down"
            style={{ padding: 4, background: 'transparent', border: '1px solid #333', borderRadius: 4, color: index === total - 1 ? '#444' : '#888', cursor: index === total - 1 ? 'default' : 'pointer' }}>
            <ChevronDown size={14} />
          </button>
          {onDuplicate && (
            <button onClick={onDuplicate} title="Duplicate segment (insert a copy below to re-trim)"
              style={{ padding: 4, background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: 'pointer' }}>
              <CopyPlus size={14} />
            </button>
          )}
          <button onClick={onDelete} title="Delete segment"
            style={{ padding: 4, background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#dc2626', cursor: 'pointer' }}>
            <Trash2 size={14} />
          </button>
          {/* Step 27 Option B: approve the underlying AI moment. The fact
              that the editor put it IN a reel is implicit approval — this
              makes it explicit so it feeds the eval gold set + future RAG. */}
          {onApprove && matchedMoment && matchedMoment.kind === 'key_moment' && (
            <button
              onClick={() => onApprove({
                tbtv_id: segment.source_tbtv_id,
                start_ms: matchedMoment.start_ms,
                end_ms: matchedMoment.end_ms,
                moment_type: matchedMoment.moment_type,
                title: matchedMoment.title,
                source: matchedMoment.source,
                detector: matchedMoment.detector,
              })}
              title="Approve the AI moment that produced this segment — adds to the eval gold set + Step 29 RAG exemplars."
              style={{ padding: '0 8px', height: 26, background: 'transparent', border: '1px solid #14532d', borderRadius: 4, color: '#4ade80', cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={12} /> Approve AI
            </button>
          )}
          {/* Step 26.5: reject the underlying AI moment so it won't re-emit
              on the next TBTV-AI run. Only available when this segment maps
              to a known key_moment (chapter-derived segments don't have a
              moment to reject). Does NOT delete this segment from the reel
              — use the Trash button next to it for that. */}
          {onReject && matchedMoment && matchedMoment.kind === 'key_moment' && (
            <button
              onClick={() => onReject({
                tbtv_id: segment.source_tbtv_id,
                start_ms: matchedMoment.start_ms,
                end_ms: matchedMoment.end_ms,
                moment_type: matchedMoment.moment_type,
                title: matchedMoment.title,
                source: matchedMoment.source,
                detector: matchedMoment.detector,
              })}
              title="Reject the AI moment that produced this segment — skipped on future TBTV-AI re-runs of this video. (Does NOT delete this segment from the reel.)"
              style={{ padding: '0 8px', height: 26, background: 'transparent', border: '1px solid #7f1d1d', borderRadius: 4, color: '#fca5a5', cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Reject AI
            </button>
          )}
        </div>
      </div>

      {srcUrl && (
        <mux-player
          ref={playerRef}
          // Step 25g: key only on playbackId so the player stays mounted
          // across trim edits. The bounds-change effect above seeks the
          // playhead instead of reloading the source.
          key={playbackId}
          src={srcUrl}
          stream-type="on-demand"
          accent-color="#3b82f6"
          style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }}
        />
      )}
      {!srcUrl && (
        <div style={{ aspectRatio: '16/9', background: '#0d1622', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
          (no preview — source lacks Mux playback_id)
        </div>
      )}

      {/* Step 25g: bounds readout + Play clip. The player loads the full
          source so the editor knows where the trimmed clip starts/ends and
          can play it from In with auto-pause at Out. Any manual scrub
          cancels Play clip mode so the auto-pause doesn't hijack their
          investigation. */}
      {srcUrl && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '6px 8px', background: '#0d1622',
          border: '1px solid #1a1a1a', borderRadius: 4, fontSize: 11,
        }}>
          <IconButton onClick={playClip} title="Seek to In and play through Out (auto-pauses)">
            <Play size={12} /> Play clip
          </IconButton>
          <span style={{ color: '#7dd3fc', fontFamily: 'monospace' }}>
            In {fmtTimeInput(segment.start_ms)}
          </span>
          <span style={{ color: '#666' }}>→</span>
          <span style={{ color: '#7dd3fc', fontFamily: 'monospace' }}>
            Out {fmtTimeInput(segment.end_ms)}
          </span>
          <span style={{ color: '#888', fontFamily: 'monospace' }}>
            · {fmtTime(segment.end_ms - segment.start_ms)} clip
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ color: '#555', fontFamily: 'monospace', fontSize: 10 }}>
            scrub player freely — only Play clip auto-pauses at Out
          </span>
        </div>
      )}

      {/* Playhead-driven in/out controls — Mux Editor style.
          [In: 1:23.45]  [Set In ↘]  [⇤ go to In]      [⇥ go to Out]  [↘ Set Out]  [Out: 2:45.30] */}
      {srcUrl && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          padding: '6px 8px', background: '#0d1622', border: '1px solid #1a1a1a', borderRadius: 4,
        }}>
          <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4 }}>In</span>
          <TimeInput
            valueMs={segment.start_ms}
            maxMs={Math.max(0, segment.end_ms - 250)}
            onCommit={(ms) => onChange({ start_ms: Math.max(0, ms), end_ms: segment.end_ms })}
          />
          <IconButton onClick={() => shiftIn(-10000)} title="Shift In 10s earlier (more lead-up)">
            <ChevronLeft size={14} /> 10s
          </IconButton>
          <IconButton onClick={() => shiftIn(10000)} title="Shift In 10s later (less lead-up)">
            10s <ChevronRight size={14} />
          </IconButton>
          <IconButton onClick={setIn} title="Set In to the current playhead frame in the preview above">
            <SquareDot size={12} /> Set In
          </IconButton>
          <div style={{ flex: 1, minWidth: 20 }} />
          <IconButton onClick={setOut} title="Set Out to the current playhead frame in the preview above">
            Set Out <SquareDot size={12} />
          </IconButton>
          <IconButton onClick={() => shiftOut(-10000)} title="Shift Out 10s earlier (less tail)">
            <ChevronLeft size={14} /> 10s
          </IconButton>
          <IconButton onClick={() => shiftOut(10000)} title="Shift Out 10s later (more tail)">
            10s <ChevronRight size={14} />
          </IconButton>
          <TimeInput
            valueMs={segment.end_ms}
            minMs={segment.start_ms + 250}
            maxMs={durationMs > 0 ? durationMs : undefined}
            onCommit={(ms) => onChange({ start_ms: segment.start_ms, end_ms: ms })}
          />
          <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginLeft: 4 }}>Out</span>
          {/* Step 25g Reset: restores clip to the bounds it had when this
              editor session began (or when it was inserted via the picker).
              Greyed out when current bounds already match the original. */}
          {(() => {
            const origStart = segment._orig_start_ms
            const origEnd = segment._orig_end_ms
            const canReset = origStart != null && origEnd != null
              && (origStart !== segment.start_ms || origEnd !== segment.end_ms)
            return (
              <IconButton
                onClick={canReset ? () => onChange({ start_ms: origStart, end_ms: origEnd }) : undefined}
                title={canReset
                  ? `Reset to original bounds (${fmtTimeInput(origStart)} → ${fmtTimeInput(origEnd)})`
                  : 'Already at original bounds'}
                disabled={!canReset}
              >
                ↺ Reset
              </IconButton>
            )
          })()}
        </div>
      )}

      {durationMs > 0 && (
        <>
          {/* Zoom control — adjusts how much of the source is visible on the
              trim track. Far left = full source (coarse drag, 1px ≈ many
              seconds for long videos). Far right = tightly-framed clip
              (fine drag, 1px ≈ tenths of a second). View auto-centers on
              the clip midpoint and clamps to source bounds. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 8px', background: '#0d1622',
            border: '1px solid #1a1a1a', borderRadius: 4, fontSize: 11,
          }}>
            <ZoomOut size={12} style={{ color: '#888', flexShrink: 0 }} />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: '#3b82f6' }}
              title={`Zoom: ${viewWidthMs > 0 ? fmtTime(viewWidthMs) : '—'} visible / ${fmtTime(durationMs)} total`}
            />
            <ZoomIn size={12} style={{ color: '#888', flexShrink: 0 }} />
            <span style={{ color: '#7dd3fc', fontFamily: 'monospace', minWidth: 80, textAlign: 'right' }}>
              {viewWidthMs > 0 ? fmtTime(viewWidthMs) : '—'} view
            </span>
          </div>
          <TrimRangeSlider
            minMs={0}
            maxMs={durationMs}
            viewMinMs={viewMinMs}
            viewMaxMs={viewMaxMs}
            startMs={segment.start_ms}
            endMs={segment.end_ms}
            playheadMs={playheadMs}
            onSeek={seekTo}
            onDragStart={() => setFrozenView({ viewMinMs, viewMaxMs })}
            onChangeCommitted={() => setFrozenView(null)}
            onChange={({ startMs, endMs }) => onChange({ start_ms: startMs, end_ms: endMs })}
          />
        </>
      )}
      {durationMs === 0 && (
        <div style={{ fontSize: 11, color: '#f59e0b' }}>
          Source duration unknown — slider disabled. Use the time inputs + Set In/Out from the playhead above.
        </div>
      )}
    </div>
  )
}

// Editable mm:ss.ss input that commits on Enter or blur. Invalid entries
// are rejected and the field reverts to the canonical formatted value.
// Clamped within [minMs, maxMs] when those bounds are provided.
function TimeInput({ valueMs, minMs = 0, maxMs, onCommit }) {
  const canonical = fmtTimeInput(valueMs)
  const [local, setLocal] = useState(canonical)
  const [bad, setBad] = useState(false)
  // Keep local in sync when valueMs changes from outside (slider drag, Set In/Out).
  useEffect(() => {
    setLocal(fmtTimeInput(valueMs))
    setBad(false)
  }, [valueMs])

  function commit() {
    if (local === canonical) return
    const ms = parseTimeInput(local)
    if (ms == null) { setBad(true); return }
    let clamped = ms
    if (clamped < minMs) clamped = minMs
    if (maxMs != null && clamped > maxMs) clamped = maxMs
    if (clamped === valueMs) {
      setLocal(canonical)
      setBad(false)
      return
    }
    setBad(false)
    onCommit(clamped)
  }

  return (
    <input
      type="text"
      value={local}
      onChange={(e) => { setLocal(e.target.value); setBad(false) }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur() }
        else if (e.key === 'Escape') { setLocal(canonical); setBad(false); e.currentTarget.blur() }
      }}
      title="mm:ss.ss — type a new value and press Enter"
      style={{
        width: 76, padding: '3px 6px',
        background: '#0a0a0a',
        border: '1px solid ' + (bad ? '#dc2626' : '#2a2a2a'),
        borderRadius: 3, color: bad ? '#fca5a5' : '#bfdbfe',
        fontSize: 11, fontFamily: 'monospace',
        textAlign: 'center',
      }}
    />
  )
}

function IconButton({ onClick, title, children, disabled = false }) {
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{
        padding: '3px 8px', fontSize: 11,
        background: 'transparent', border: '1px solid #333', borderRadius: 3,
        color: disabled ? '#555' : '#bfdbfe',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
      }}>
      {children}
    </button>
  )
}
